import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ErrorSnackbar from '../../components/ErrorSnackbar/ErrorSnackbar';
import SubmitButton from '../../components/SubmitButton/SubmitButton';
import SuccessMessage from '../../components/SuccessMessage/SuccessMessage';
import TopBar from '../../components/TopBar/TopBar';
import { InitialData } from '../../models/InitialData';
import { RoleType } from '../../models/RoleType';
import { SourceType } from '../../models/SourceType';
import { SourceQuestionMap } from '../../models/SourceQuestionMap';
import { RequestBody } from '../../models/RequestBody';

const DEFAULT_QUESTION = 'How satisfied are you with this textbook?';

const SOURCE_QUESTIONS: SourceQuestionMap = {
  practice: 'How satisfied are you with FlatWorld Homework?',
  scholar: DEFAULT_QUESTION,
};

export default function SurveyContainer() {
  const [uuid, setUuid] = useState<string>('');
  const [role, setRole] = useState<RoleType>();
  const [source, setSource] = useState<SourceType>();
  const [bookId, setBookId] = useState<number>();
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string>('');

  const [initialized, setInitialized] = useState<boolean>(false);
  const [populated, setPopulated] = useState<boolean>(false);
  const [declined, setDeclined] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const destroy = useCallback(() => {
    postMessage('destroyConcierge');
  }, []);

  const getSurveyable = useCallback((data: InitialData) => {
    const params = {
      'uuid': data.uuid,
      'source': data.source,
      'book_id': `${data.book_id}`,
    };

    fetch(`/api/surveyable?${new URLSearchParams(params)}`)
      .then((res) => {
        if (!res.ok) { throw Error(); }

        setPopulated(true);
        postMessage('displayConcierge');
      })
      .catch(() => destroy());
  }, [destroy]);

  // Before initialized is true, send message to parent app to populate Concierge
  useEffect(() => {
    if (initialized) { return; }

    postMessage('populateConcierge');

    setInitialized(true);
  }, [initialized]);

  // Populate via data received from parent app
  useEffect(() => {
    window.addEventListener('message', (event: MessageEvent) => {
      const data: InitialData = event.data;
      if (!hasAllData(data) || data.action !== 'populateConcierge') { return; }

      setUuid(data.uuid);
      setRole(data.role);
      setSource(data.source);
      setBookId(data.book_id);

      getSurveyable(data);
    });
  }, [getSurveyable]);

  const hasAllData = (data: any) => {
    return data &&
      data.uuid &&
      data.role &&
      data.source &&
      data.book_id;
  };

  const generateRequestOptions = (onDecline = false): RequestInit => {
    const requestBody: Partial<RequestBody> = {
      uuid,
      context_role: role,
      source: source,
      book_id: bookId,
      declined: onDecline,
    };
    if (!onDecline) { requestBody['survey_response'] = { feedback, rating }; }

    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    };
  };

  const postMessage = (message: string) => {
    window.parent.postMessage(message, '*');
  };

  const handleClose = () => {
    destroy();
  };

  const handleDecline = () => {
    setDeclined(true);
    setSubmitting(true);

    fetch('/api/responses', generateRequestOptions(true))
      .then(() => destroy());
  };

  const handleSubmit = () => {
    setSubmitting(true);

    fetch('/api/responses', generateRequestOptions())
      .then((res) => {
        if (!res.ok) { throw Error(); }
        setSubmitting(false);
        setSubmitted(true);
        setTimeout(() => destroy(), 3000);
      })
      .catch(() => {
        setSubmitting(false);
        setError(true);
      });
  };

  return (
    <>
      {populated && 
        <>
          <TopBar
            showDecline={!submitted && !error}
            disabled={submitting}
            onClose={() => handleClose()}
            onDecline={() => handleDecline()}
          />
          <Box
            component="form"
            autoComplete="off"
            sx={{ p: 2 }}>
            {submitted
              ?
              <SuccessMessage />
              :
              <>
                <Stack spacing={2} sx={{ cursor: submitting ? 'wait' : 'auto' }}>
                  <Typography component="legend">
                    {source ? SOURCE_QUESTIONS[source] : DEFAULT_QUESTION}
                  </Typography>
                  <Rating
                    name="rating"
                    value={rating}
                    size="large"
                    disabled={submitting}
                    onChange={(_e, value) => setRating(value)}
                  />

                  <Typography component="legend">
                    Would you like to leave additional feedback?
                  </Typography>
                  <TextField
                    id="feedback"
                    label="Write message..."
                    variant="standard"
                    multiline
                    rows={3}
                    disabled={submitting}
                    onChange={e => setFeedback(e.target.value)}
                  />

                  <SubmitButton
                    submitting={submitting && !declined}
                    disabled={submitting || !rating}
                    onClick={handleSubmit}
                  />
                </Stack>

                {error &&
                  <ErrorSnackbar
                    error={error}
                    onClose={() => setError(false)}
                  />
                }
              </>
            }
          </Box>
        </>
      }
    </>
  );
}
